import { FieldArray, FieldArrayPath, UseFieldArrayReturn } from 'react-hook-form';
import { Button } from '@givebutter/primitives/button';
import { Label } from '@givebutter/primitives/label';
import { ContactFormValues } from './types';
import { UseFormCheckboxInput, UseFormError, useGivebutterFormContext } from 'components/form';

type ContactFormArrayFields =
    | 'phones'
    | 'emails'
    | 'pointOfContactEmails'
    | 'pointOfContactPhones'
    | 'companyEmails'
    | 'companyPhones';

interface ArrayFieldActionsProps<T extends ContactFormArrayFields>
    extends UseFieldArrayReturn<ContactFormValues, T> {
    index: number;
    label: string;
    path: FieldArrayPath<Pick<ContactFormValues, ContactFormArrayFields>>;
    appendValue: FieldArray<ContactFormValues, T>;
}

const MAX_ARRAY_FIELDS = 5;

const ContactFormArrayFieldActions = <T extends ContactFormArrayFields>(
    props: ArrayFieldActionsProps<T>,
) => {
    const { index, appendValue, path, label, fields, append, remove } = props;
    const { control, getValues, setValue, watch } = useGivebutterFormContext<ContactFormValues>();
    const field = watch(`${path}.${index}`);
    const isPrimary = field.isPrimary;
    const hasMany = fields.length > 1;

    return (
        <div className={'flex flex-col gap-2'}>
            <div className={'flex items-center justify-between gap-2 empty:hidden'}>
                {hasMany ? (
                    <div className={'flex items-center gap-2'}>
                        <Label>{isPrimary ? 'Primary' : 'Make Primary'}</Label>
                        <UseFormCheckboxInput
                            control={control}
                            name={`${path}.${index}.isPrimary`}
                            className={
                                'radix-disabled:!border-blue-700 radix-disabled:!bg-blue-500 radix-disabled:[&>*]:!text-white'
                            }
                            rules={{
                                validate: _ => {
                                    if (
                                        (getValues(path) as { isPrimary: boolean }[]).every(
                                            field => !field.isPrimary,
                                        )
                                    ) {
                                        return `Please choose a primary ${label}`;
                                    }
                                },
                            }}
                            onCheckedChange={isChecked => {
                                const nextValues = getValues(path).map((field, lazyIndex) => {
                                    return {
                                        ...field,
                                        isPrimary: isChecked && index === lazyIndex,
                                    };
                                });

                                nextValues.forEach((value, index) =>
                                    setValue(`${path}.${index}`, value),
                                );
                            }}
                        />
                    </div>
                ) : null}

                {hasMany ? (
                    <Button
                        variant="none"
                        color="error"
                        className="font-medium"
                        onClick={() => remove(index)}
                    >
                        - Remove
                    </Button>
                ) : null}
            </div>
            {index === fields.length - 1 && fields.length < MAX_ARRAY_FIELDS ? (
                <Button
                    variant="none"
                    color="primary"
                    className="font-medium"
                    onClick={() => append(appendValue)}
                >
                    + Add {label}
                </Button>
            ) : null}
            <UseFormError name={`${path}.${index}.isPrimary`} />
        </div>
    );
};

export { ContactFormArrayFieldActions };
export type { ContactFormArrayFields };
